<script setup>
    import { defineProps, defineEmits } from 'vue'
    import { Dialog, DialogTitle, DialogOverlay, TransitionChild, TransitionRoot } from '@headlessui/vue'
    import { XIcon } from '@heroicons/vue/solid'

    const props = defineProps({
        open: {type: Boolean, default: false},
        title: {type: String, default: ''},
        fixed: {type: Boolean, default: false},
        width: {type: String }
    })

    const emit = defineEmits(['closeModal'])

    function close(force = false) {
        if (props.fixed && !force) return

        emit('closeModal')
    }
</script>

<template>
    <TransitionRoot
        as="template"
        :show="props.open"
    >
        <Dialog
            as="div"
            class="fixed z-40 inset-0 overflow-y-auto"
            @close="close()"
        >
            <div
                class="flex items-end justify-center min-h-screen pt-4 px-4   text-center sm:block sm:p-0 dark:bg-neutral-600 dark:bg-opacity-60"
            >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-200"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-100"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay
                        class="fixed inset-0 bg-neutral-400 dark:bg-neutral-800 dark:bg-opacity-60 bg-opacity-60 transition-opacity backdrop-blur-sm"
                    />
                </TransitionChild>

                <span
                    class="hidden sm:inline-block sm:align-middle sm:h-screen"
                    aria-hidden="true"
                >&#8203;</span>

                <TransitionChild
                    as="template"
                    enter="ease-out duration-200"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-100"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        :class="[
                            'inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left shadow-xl', // removido overflow-hidden 
                            'transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6',
                            'dark:bg-neutral-600',
                            props.width
                        ]"
                    >
                        <div
                            v-if="!props.fixed"
                            class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"
                        >
                            <button
                                type="button"
                                :class="[
                                    'bg-white rounded-md text-gray-400',
                                    'chat_transition hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:text-gray-500',
                                    'dark:bg-neutral-600 dark:hover:text-white dark:focus:ring-white dark:focus:text-white',
                                ]"
                                @click="close()"
                            >
                                <span class="sr-only">Fechar</span>

                                <XIcon
                                    class="h-6 w-6"
                                    aria-hidden="true"
                                />
                            </button>
                        </div>

                        <div class="sm:flex sm:items-start">
                            <div class="w-full mt-3 text-center sm:mt-0 sm:text-left">
                                <DialogTitle
                                    as="h3"
                                    class="text-lg leading-6 font-medium text-gray-900 dark:text-gray-300"
                                >
                                    {{ props.title }}
                                </DialogTitle>

                                <div class="flex w-full mt-2">
                                    <slot />
                                </div>
                            </div>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>